import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { apiService } from '@/services/api'
import { authTokenManager } from '@/utils/authTokenManager'
import { FEATURE_FLAGS } from '@/config/featureFlags'
import { useUserInit } from '@/contexts/UserInitContext'
import type { CurrentTeamResponse, ListTeamsResponse, TeamMemberRole, TeamMember } from '@shared/types'

/**
 * Chief query keys are swept wholesale on a team switch.
 *
 * Chief data is entitled per account (teamId ?? userId), but its query keys do
 * not carry the team id, so nothing about a key changes when the active team
 * changes. This prefix sweep is the only thing that clears the previous team's
 * answers, including the cached access probe in useChiefAccess: without it a
 * user switching out of an entitled team keeps a working Chief shell over calls
 * that all 403, and a user switching INTO one sees no Chief until the cache
 * goes stale.
 *
 * Exported so the coupling can be asserted against the key the hook actually
 * caches under, rather than both sides being pinned to a string literal that a
 * rename could move independently.
 */
export const isChiefTeamScopedQueryKey = (queryKey: readonly unknown[]): boolean =>
  String(queryKey[0]).startsWith('chief-')

const isTeamInvitePermissionError = (error: unknown): boolean => {
  const maybeError = error as { response?: { status?: number; data?: { error?: string } } }
  return (
    maybeError.response?.status === 403 &&
    maybeError.response?.data?.error === 'Admin access required'
  )
}

export function useTeam() {
  const queryClient = useQueryClient()
  const { isSignedIn, isLoaded, userId } = useAuth()
  const { isInitialized } = useUserInit()

  // Only enable queries when:
  // 1. Teams feature is enabled
  // 2. Clerk auth is loaded
  // 3. User is signed in
  // 4. Auth token manager is ready
  // 5. User has been initialized in the database (prevents race condition errors)
  const isAuthReady = isLoaded && isSignedIn && authTokenManager.isReady()
  const shouldEnableTeamQueries = FEATURE_FLAGS.TEAMS_ENABLED && isAuthReady && isInitialized

  // Query: Get current active team
  const {
    data: currentTeam,
    isLoading: isLoadingCurrentTeam,
    error: currentTeamError,
    refetch: refetchCurrentTeam
  } = useQuery({
    queryKey: ['team', 'current', userId],
    queryFn: async () => {
      const response = await apiService.getCurrentTeam()
      return response.data
    },
    enabled: shouldEnableTeamQueries, // Only fetch when teams feature is enabled and auth is ready
    staleTime: 30000 // Cache for 30 seconds
  })

  // Query: List all teams user is part of
  const {
    data: allTeams,
    isLoading: isLoadingAllTeams,
    error: allTeamsError,
    refetch: refetchAllTeams
  } = useQuery({
    queryKey: ['team', 'list', userId],
    queryFn: async () => {
      const response = await apiService.listTeams()
      return response.data
    },
    enabled: shouldEnableTeamQueries, // Only fetch when teams feature is enabled and auth is ready
    staleTime: 30000 // Cache for 30 seconds
  })

  // Query: Get team members
  const {
    data: teamMembers,
    isLoading: isLoadingMembers,
    isFetching: isFetchingMembers,
    error: membersError,
    refetch: refetchMembers
  } = useQuery({
    queryKey: ['team', 'members', userId, currentTeam?.team?.id],
    queryFn: async () => {
      const response = await apiService.getTeamMembers()
      return response.data
    },
    enabled: shouldEnableTeamQueries && !!currentTeam?.team?.id, // Only fetch when teams feature is enabled, auth is ready, AND we have a team
    staleTime: 30000
  })

  // Mutation: Switch team
  const switchTeamMutation = useMutation({
    mutationFn: (teamId: string) => apiService.switchTeam(teamId),
    onSuccess: () => {
      // Invalidate team and user queries
      queryClient.invalidateQueries({ queryKey: ['team'] })
      queryClient.invalidateQueries({ queryKey: ['user'] })
      queryClient.invalidateQueries({ queryKey: ['user-profile'] })

      // Invalidate all team-scoped data queries
      // This ensures data refreshes immediately when switching teams
      queryClient.invalidateQueries({ queryKey: ['domains'] })
      queryClient.invalidateQueries({ queryKey: ['unified-domains'] })
      queryClient.invalidateQueries({ queryKey: ['urls'] })
      queryClient.invalidateQueries({ queryKey: ['urlSchemas'] })
      queryClient.invalidateQueries({ queryKey: ['user-stats'] })
      queryClient.invalidateQueries({ queryKey: ['user-credits'] })
      queryClient.invalidateQueries({ queryKey: ['generation-history'] })
      queryClient.invalidateQueries({ queryKey: ['hubspot-connections'] })
      queryClient.invalidateQueries({ queryKey: ['hubspot-team-connections'] })
      queryClient.invalidateQueries({ queryKey: ['aba-sites'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard-urls'] })

      // Invalidate domain detail page queries (team-scoped via teamId in key)
      queryClient.invalidateQueries({ queryKey: ['domain'] })
      queryClient.invalidateQueries({ queryKey: ['domain-summary'] })
      queryClient.invalidateQueries({ queryKey: ['domain-urls'] })
      queryClient.invalidateQueries({ queryKey: ['domain-urls-count'] })
      queryClient.invalidateQueries({ queryKey: ['domain-limits'] })
      queryClient.invalidateQueries({ queryKey: ['unified-page'] })
      queryClient.invalidateQueries({ queryKey: ['aba-overview'] })
      queryClient.invalidateQueries({ queryKey: ['aba-timeline'] })
      queryClient.invalidateQueries({ queryKey: ['aba-bots'] })
      queryClient.invalidateQueries({ queryKey: ['aba-recent-visit'] })

      // Invalidate billing/settings-related queries
      queryClient.invalidateQueries({ queryKey: ['current-subscription'] })
      queryClient.invalidateQueries({ queryKey: ['tier-usage'] })
      queryClient.invalidateQueries({ queryKey: ['billing-overview'] })
      queryClient.invalidateQueries({ queryKey: ['billing'] })
      queryClient.invalidateQueries({ queryKey: ['all-invoices'] })
      queryClient.invalidateQueries({ queryKey: ['organizations'] })

      queryClient.invalidateQueries({ predicate: (q) => isChiefTeamScopedQueryKey(q.queryKey) })
    }
  })

  // Mutation: Create team invite (with optional role)
  const createInviteMutation = useMutation({
    mutationFn: (role?: 'admin' | 'member') => apiService.createTeamInvite(role),
    onSuccess: () => {
      // Refetch invites after creating one
      queryClient.invalidateQueries({ queryKey: ['team', 'invites'] })
      queryClient.invalidateQueries({ queryKey: ['team', 'current'] })
    }
  })

  // Mutation: Remove team member
  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => apiService.removeTeamMember(userId),
    onSuccess: () => {
      // Refetch team members after removing one
      queryClient.invalidateQueries({ queryKey: ['team', 'members'] })
      queryClient.invalidateQueries({ queryKey: ['team', 'current'] })
    }
  })

  // Mutation: Update member role
  const updateMemberRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: 'admin' | 'member' }) =>
      apiService.updateMemberRole(userId, role),
    onSuccess: () => {
      // Refetch team members after updating role
      queryClient.invalidateQueries({ queryKey: ['team', 'members'] })
    }
  })

  // Mutation: Leave team
  const leaveTeamMutation = useMutation({
    mutationFn: () => apiService.leaveTeam(),
    onSuccess: () => {
      // Only invalidate team and user queries
      queryClient.invalidateQueries({ queryKey: ['team'] })
      queryClient.invalidateQueries({ queryKey: ['user'] })
    }
  })

  // Mutation: Accept team invite
  const acceptInviteMutation = useMutation({
    mutationFn: (token: string) => apiService.acceptTeamInvite(token),
    onSuccess: () => {
      // Only invalidate team and user queries
      queryClient.invalidateQueries({ queryKey: ['team'] })
      queryClient.invalidateQueries({ queryKey: ['user'] })
    }
  })

  // Mutation: Create new team
  const createTeamMutation = useMutation({
    mutationFn: (organizationName?: string) => apiService.createTeam(organizationName),
    onSuccess: () => {
      // Only invalidate team and user queries
      queryClient.invalidateQueries({ queryKey: ['team'] })
      queryClient.invalidateQueries({ queryKey: ['user'] })
    }
  })

  // Compute if user can invite (needed before teamInvites query to prevent 403 errors).
  // Prefer the authoritative value from /team/current; member lists can briefly be stale
  // after team switching or cache hydration.
  const userCanInvite = (() => {
    if (typeof currentTeam?.canInvite === 'boolean') return currentTeam.canInvite
    if (!teamMembers?.members || !userId) return false
    const currentMember = teamMembers.members.find((m: TeamMember) => m.userId === userId)
    return currentMember?.role === 'owner' || currentMember?.role === 'admin'
  })()

  // Query: Get team invites list
  // Only fetch if user has permission (owner/admin) to prevent 403 errors
  // Wait until teamMembers is fully loaded (not just cached) AND not actively refetching
  // to avoid stale cache permission issues that cause 403 errors
  const {
    data: teamInvites,
    isLoading: isLoadingInvites,
    error: invitesError,
    refetch: refetchInvites
  } = useQuery({
    queryKey: ['team', 'invites', userId, currentTeam?.team?.id],
    queryFn: async () => {
      try {
        const response = await apiService.getTeamInvites()
        return response.data
      } catch (error) {
        if (isTeamInvitePermissionError(error)) {
          return []
        }
        throw error
      }
    },
    enabled: shouldEnableTeamQueries && !!currentTeam?.team?.id && userCanInvite && !isLoadingMembers && !isFetchingMembers,
    staleTime: 30000
  })

  // Mutation: Delete team invite
  const deleteInviteMutation = useMutation({
    mutationFn: (inviteId: string) => apiService.deleteTeamInvite(inviteId),
    onSuccess: () => {
      // Refetch invites after deletion
      queryClient.invalidateQueries({ queryKey: ['team', 'invites'] })
    }
  })

  return {
    // Current team data
    currentTeam: currentTeam as CurrentTeamResponse | undefined,
    isLoadingCurrentTeam,
    currentTeamError,
    refetchCurrentTeam,

    // All teams data
    allTeams: allTeams as ListTeamsResponse | undefined,
    isLoadingAllTeams,
    allTeamsError,
    refetchAllTeams,

    // Team members data
    teamMembers,
    isLoadingMembers,
    membersError,
    refetchMembers,

    // Mutations
    switchTeam: switchTeamMutation.mutateAsync,
    isSwitchingTeam: switchTeamMutation.isPending,
    switchTeamError: switchTeamMutation.error,

    createInvite: createInviteMutation.mutateAsync,
    isCreatingInvite: createInviteMutation.isPending,
    createInviteError: createInviteMutation.error,

    removeMember: removeMemberMutation.mutateAsync,
    isRemovingMember: removeMemberMutation.isPending,
    removeMemberError: removeMemberMutation.error,

    updateMemberRole: updateMemberRoleMutation.mutateAsync,
    isUpdatingMemberRole: updateMemberRoleMutation.isPending,
    updateMemberRoleError: updateMemberRoleMutation.error,

    leaveTeam: leaveTeamMutation.mutateAsync,
    isLeavingTeam: leaveTeamMutation.isPending,
    leaveTeamError: leaveTeamMutation.error,

    acceptInvite: acceptInviteMutation.mutateAsync,
    isAcceptingInvite: acceptInviteMutation.isPending,
    acceptInviteError: acceptInviteMutation.error,

    createTeam: createTeamMutation.mutateAsync,
    isCreatingTeam: createTeamMutation.isPending,
    createTeamError: createTeamMutation.error,

    // Team invites data
    teamInvites,
    isLoadingInvites,
    invitesError,
    refetchInvites,

    deleteInvite: deleteInviteMutation.mutateAsync,
    isDeletingInvite: deleteInviteMutation.isPending,
    deleteInviteError: deleteInviteMutation.error,

    // Computed properties
    isTeamOwner: currentTeam?.activeMembership?.isOwner ?? currentTeam?.isOwner ?? false,
    hasMultipleTeams: (allTeams?.count ?? 0) > 1,
    ownsTeam: allTeams?.teams.some((t: any) => t.isOwner) ?? false,
    isLoading: isLoadingCurrentTeam || isLoadingAllTeams,

    // Role-based computed properties
    currentUserRole: (() => {
      if (currentTeam?.activeMembership?.role) return currentTeam.activeMembership.role
      if (currentTeam?.currentUserRole) return currentTeam.currentUserRole
      if (currentTeam?.isOwner) return 'owner' as TeamMemberRole
      if (!teamMembers?.members || !userId) return null
      const currentMember = teamMembers.members.find((m: TeamMember) => m.userId === userId)
      return currentMember?.role ?? null
    })(),

    // Permission helpers based on role
    // Prefer authoritative permissions from /team/current to avoid stale member cache issues.
    canInvite: (() => {
      if (typeof currentTeam?.canInvite === 'boolean') return currentTeam.canInvite
      if (!teamMembers?.members || !userId) return false
      const currentMember = teamMembers.members.find((m: TeamMember) => m.userId === userId)
      return currentMember?.role === 'owner' || currentMember?.role === 'admin'
    })(),

    canRemoveMember: (() => {
      if (typeof currentTeam?.canRemoveMember === 'boolean') return currentTeam.canRemoveMember
      if (!teamMembers?.members || !userId) return false
      const currentMember = teamMembers.members.find((m: TeamMember) => m.userId === userId)
      return currentMember?.role === 'owner' || currentMember?.role === 'admin'
    })(),

    canManageBilling: (() => {
      if (typeof currentTeam?.canManageBilling === 'boolean') return currentTeam.canManageBilling
      if (!teamMembers?.members || !userId) return false
      const currentMember = teamMembers.members.find((m: TeamMember) => m.userId === userId)
      return currentMember?.role === 'owner' || currentMember?.role === 'admin'
    })(),

    canUpdateRoles: (() => {
      if (typeof currentTeam?.canUpdateRoles === 'boolean') return currentTeam.canUpdateRoles
      if (!teamMembers?.members || !userId) return false
      const currentMember = teamMembers.members.find((m: TeamMember) => m.userId === userId)
      return currentMember?.role === 'owner'
    })()
  }
}
